import { z } from 'zod';
import { IsoTime, ServerRef } from './basics.js';
import { DomainName } from './web-domain.js';
/**
 * Block rules (knowledge doc section 12, "Block rules"; P11.5): what a person
 * says should be stopped, beyond bots by user agent and single addresses.
 *
 * Two kinds, by what it takes to decide:
 *
 * - **request**: one request is enough — its path, and its method. The web server
 *   answers it 403 (refused, for clients that may be legitimate) or 444 (Nginx
 *   closes the connection without a word, for traffic that is not), from
 *   Bartizo's generated include. The client is not stopped, only that request.
 * - **behaviour**: a count over time — "20 refused requests for `.php` paths in a
 *   minute" — which CrowdSec decides (rule 11) from a scenario Bartizo generates,
 *   reading Bartizo's own access log; the address is banned for `ban_seconds`.
 *
 * **Where a rule applies.** Rules live in the settings layers, global → group →
 * server, and a rule may name a `site` (the request's host). For one site, a rule
 * written for that site replaces the rule of the same `id` without one, whichever
 * layer either was written in; otherwise the lowest layer's version of a rule
 * wins. So a site can tighten, loosen, watch or switch off (`enabled: false`) a
 * rule the fleet has, and nothing set lower means the higher level applies.
 *
 * Unlike the bot lists, a lower level can therefore lift a protection a wider
 * one set — on purpose, because a rule that is right for the fleet can be wrong
 * for one application (`.php` on a PHP site). It is never silent: the switched
 * off copy stays in the layer, the change is audited, and the effective rules of
 * a site say where each one came from.
 *
 * **Rule 18.** Everything that reaches a configuration file is a literal from a
 * small character set: a path, a host name, a method from a fixed list, numbers.
 * Nothing is a regular expression anybody typed.
 */
/** A rule's name in its layer: lower case, digits and dashes, as in `php-probe`. */
export const BlockRuleId = z
    .string()
    .regex(/^[a-z0-9][a-z0-9-]{0,47}$/, 'lower-case letters, digits and dashes');
/**
 * The characters a path literal may hold: what URL paths are made of, less
 * anything a configuration language quotes or escapes with (`" ' \ $ { } ; #`
 * and spaces). A path with others is matched by a shorter literal around them.
 */
export const PATH_LITERAL = /^[A-Za-z0-9._~\-/%+@:,=!*]+$/;
export const PathLiteral = z
    .string()
    .min(1)
    .max(200)
    .regex(PATH_LITERAL, 'letters, digits and . _ ~ - / % + @ : , = ! * only');
export const PathMatchKind = z.enum(['exact', 'prefix', 'suffix']);
/**
 * One path condition. Matched without regard to case, against the path without
 * its query string: `/.ENV` is a probe for `/.env` too.
 */
export const PathMatch = z
    .object({ kind: PathMatchKind, value: PathLiteral })
    .strict()
    .superRefine((match, ctx) => {
    if (match.kind !== 'suffix' && !match.value.startsWith('/')) {
        ctx.addIssue({ code: 'custom', path: ['value'], message: 'a path starts with /' });
    }
    if (match.kind === 'prefix' && match.value === '/') {
        ctx.addIssue({
            code: 'custom',
            path: ['value'],
            message: 'every path starts with /: name a narrower one',
        });
    }
    if (match.kind === 'suffix' && match.value.length < 2) {
        ctx.addIssue({ code: 'custom', path: ['value'], message: 'at least two characters' });
    }
});
export const HttpMethod = z.enum(['GET', 'HEAD', 'POST', 'PUT', 'PATCH', 'DELETE', 'OPTIONS']);
/** `forbidden` is 403; `drop` is Nginx's 444, which Apache does not have (it answers 403). */
export const BlockResponse = z.enum(['forbidden', 'drop']);
/** `watch` counts what a rule would match and stops nothing. */
export const BlockRuleMode = z.enum(['enforce', 'watch']);
export const BLOCK_RULE_CAPS = {
    /** In one layer, every site's together. */
    rules: 100,
    paths: 20,
    statuses: 10,
};
/** Section 12's ban limits: four hours by default, thirty days at most. */
export const BLOCK_RULE_BAN_DEFAULT_SECONDS = 4 * 60 * 60;
export const BLOCK_RULE_BAN_MAX_SECONDS = 30 * 24 * 60 * 60;
/**
 * Whether a behaviour rule can only catch something other than ordinary
 * browsing: it names a path, or counts only refused or failed answers. Checked
 * by the schema and again by the helper, so neither a slip nor a compromised
 * agent can have CrowdSec ban every visitor.
 */
export const countsOnlyAttacks = (rule) => rule.paths.length > 0 ||
    (rule.statuses.length > 0 && rule.statuses.every((status) => status >= 400));
const Common = {
    id: BlockRuleId,
    /** For a person: what the rule is for. */
    name: z
        .string()
        .trim()
        .min(1)
        .max(100)
        .regex(/^[^\p{Cc}]+$/u, 'no control characters'),
    /** The host this copy applies to; absent for every site. */
    site: DomainName.optional(),
    enabled: z.boolean(),
    mode: BlockRuleMode,
    paths: z.array(PathMatch).max(BLOCK_RULE_CAPS.paths).default([]),
    /** Empty: any method. */
    methods: z.array(HttpMethod).max(HttpMethod.options.length).default([]),
};
export const RequestBlockRule = z
    .object({
    ...Common,
    kind: z.literal('request'),
    response: BlockResponse,
})
    .strict()
    .refine((rule) => rule.paths.length > 0, {
    path: ['paths'],
    message: 'a request rule names at least one path',
});
export const BehaviourBlockRule = z
    .object({
    ...Common,
    kind: z.literal('behaviour'),
    /** The response statuses that count, as `404`; empty counts every response. */
    statuses: z.array(z.number().int().min(100).max(599)).max(BLOCK_RULE_CAPS.statuses).default([]),
    /** This many matching requests from one address… */
    count: z.number().int().min(2).max(1000),
    /** …within this many seconds is an attack. */
    within_seconds: z.number().int().min(10).max(3600),
    ban_seconds: z.number().int().min(60).max(BLOCK_RULE_BAN_MAX_SECONDS),
})
    .strict()
    // Without a path, only refused or failed answers may count: a rule counting
    // every page any visitor is served would ban ordinary visitors by the dozen.
    .refine((rule) => countsOnlyAttacks(rule), {
    path: ['paths'],
    message: 'a behaviour rule names a path, or counts only error answers (4xx, 5xx): otherwise it would ban ordinary visitors',
});
/**
 * A hub scenario's name, `author/name`. `bartizo/` is the behaviour rules' own
 * (their scenarios are generated, not the hub's) and is refused here.
 */
export const SCENARIO_NAME = /^(?!bartizo\/)[a-z0-9][a-z0-9_.-]{0,63}\/[a-z0-9][a-z0-9_.-]{0,95}$/;
export const ScenarioName = z
    .string()
    .regex(SCENARIO_NAME, 'a hub scenario, as author/name (for example crowdsecurity/ssh-bf)');
export const TRIGGER_RULE_CAPS = { scenarios: 20 };
/** What CrowdSec decides when the scenario fires: `captcha` only where a bouncer can show one. */
export const TriggerResponse = z.enum(['ban', 'captcha']);
/**
 * A CrowdSec trigger (P11.16): when one of these scenarios fires on a server, do
 * this. The decision stays CrowdSec's (rule 11): Bartizo writes a profile and
 * CrowdSec applies it — which is the only way to ban for a scenario the hub marks
 * alert-only (`ssh-time-based-bf`), and the way to ban longer, or challenge, for
 * one that already bans. Per server, with no site: it is about what a server's
 * CrowdSec saw, not about a website.
 *
 * `watch` writes nothing: the scenario's alerts are events already, and the rule's
 * preview says how many addresses it would have acted on.
 */
export const TriggerBlockRule = z
    .object({
    id: BlockRuleId,
    name: Common.name,
    /** Not per site. Present only so a rule's `site` reads the same whatever its kind. */
    site: z.undefined().optional(),
    enabled: z.boolean(),
    mode: BlockRuleMode,
    kind: z.literal('trigger'),
    scenarios: z.array(ScenarioName).min(1).max(TRIGGER_RULE_CAPS.scenarios),
    response: TriggerResponse,
    ban_seconds: z.number().int().min(60).max(BLOCK_RULE_BAN_MAX_SECONDS),
})
    .strict();
export const BlockRule = z.discriminatedUnion('kind', [
    RequestBlockRule,
    BehaviourBlockRule,
    TriggerBlockRule,
]);
/** What identifies a rule in a layer: its id, for its site or for every site. */
export const blockRuleKey = (rule) => `${rule.site ?? ''}|${rule.id}`;
export const BlockRules = z
    .array(BlockRule)
    .max(BLOCK_RULE_CAPS.rules)
    .refine((rules) => new Set(rules.map(blockRuleKey)).size === rules.length, {
    message: 'each rule id once per site',
});
/**
 * The rules one site runs, from a document's merged list: every site's rules,
 * with this site's own copy of any of them in its place, and its own rules
 * besides. `site` undefined gives the rules for a site with no copies of its own.
 */
export function rulesForSite(rules, site) {
    const byId = new Map();
    for (const rule of rules)
        if (rule.site === undefined)
            byId.set(rule.id, rule);
    if (site !== undefined) {
        for (const rule of rules)
            if (rule.site === site)
                byId.set(rule.id, rule);
    }
    return [...byId.values()].sort((a, b) => a.id.localeCompare(b.id));
}
/** The sites that have a copy of a rule of their own. */
export const sitesWithRules = (rules) => [...new Set(rules.flatMap((rule) => (rule.site === undefined ? [] : [rule.site])))].sort();
const active = (rule) => rule.enabled && rule.mode === 'enforce';
/** One request rule as the web servers enforce it. */
export const ResolvedRequestRule = z
    .object({
    id: BlockRuleId,
    paths: z.array(PathMatch).min(1).max(BLOCK_RULE_CAPS.paths),
    methods: z.array(HttpMethod).max(HttpMethod.options.length),
    response: BlockResponse,
})
    .strict();
/**
 * Request rules for `set_web_block_rules`: what every site gets, and for each
 * site with copies of its own, everything it gets instead — so the web server
 * checks a site's own list and never falls through to the fleet's for it.
 */
export const RequestRuleSet = z
    .object({
    all: z.array(ResolvedRequestRule).max(BLOCK_RULE_CAPS.rules),
    sites: z
        .array(z
        .object({
        host: DomainName,
        rules: z.array(ResolvedRequestRule).max(BLOCK_RULE_CAPS.rules),
    })
        .strict())
        .max(300),
})
    .strict();
const toRequest = (rule) => ({
    id: rule.id,
    paths: rule.paths,
    methods: rule.methods,
    response: rule.response,
});
const enforcedRequests = (rules) => rules
    .filter((rule) => rule.kind === 'request' && active(rule))
    .map(toRequest);
export function resolveRequestRules(rules) {
    return {
        all: enforcedRequests(rulesForSite(rules, undefined)),
        sites: sitesWithRules(rules).map((host) => ({
            host,
            rules: enforcedRequests(rulesForSite(rules, host)),
        })),
    };
}
export const hasRequestRules = (set) => set.all.length > 0 || set.sites.some((site) => site.rules.length > 0);
/**
 * One behaviour rule as CrowdSec runs it: a scenario named `bartizo/<name>`.
 * `hosts` limits it to those sites; `except` keeps it off sites that have a copy
 * of their own. `watch` raises CrowdSec alerts and bans nobody.
 */
export const ResolvedBehaviourRule = z
    .object({
    name: z.string().regex(/^[a-z0-9][a-z0-9_-]{0,120}$/, 'a scenario name'),
    id: BlockRuleId,
    host: DomainName.optional(),
    except: z.array(DomainName).max(300),
    paths: z.array(PathMatch).max(BLOCK_RULE_CAPS.paths),
    methods: z.array(HttpMethod).max(HttpMethod.options.length),
    statuses: z.array(z.number().int().min(100).max(599)).max(BLOCK_RULE_CAPS.statuses),
    count: z.number().int().min(2).max(1000),
    within_seconds: z.number().int().min(10).max(3600),
    ban_seconds: z.number().int().min(60).max(BLOCK_RULE_BAN_MAX_SECONDS),
    watch: z.boolean(),
})
    .strict()
    .refine((rule) => countsOnlyAttacks(rule), {
    message: 'without a path, only error answers (4xx, 5xx) may count',
});
/** `bartizo/<id>` for every site, `bartizo/<id>_<host>` for one site's copy. */
export const scenarioNameFor = (id, host) => host === undefined ? id : `${id}_${host.replace(/[^a-z0-9-]/g, '_')}`.slice(0, 121);
/** The scenarios CrowdSec should run: one per rule, and one per site's own copy. */
export function resolveBehaviourRules(rules) {
    const out = [];
    const copies = (id) => rules
        .filter((rule) => rule.id === id && rule.site !== undefined)
        .map((rule) => rule.site ?? '');
    for (const rule of rules) {
        if (rule.kind !== 'behaviour' || !rule.enabled)
            continue;
        out.push({
            name: scenarioNameFor(rule.id, rule.site),
            id: rule.id,
            ...(rule.site === undefined ? {} : { host: rule.site }),
            except: rule.site === undefined ? copies(rule.id).sort() : [],
            paths: rule.paths,
            methods: rule.methods,
            statuses: rule.statuses,
            count: rule.count,
            within_seconds: rule.within_seconds,
            ban_seconds: rule.ban_seconds,
            watch: rule.mode === 'watch',
        });
    }
    return out.sort((a, b) => a.name.localeCompare(b.name));
}
/** One trigger as CrowdSec's profile runs it. */
export const ResolvedTriggerRule = z
    .object({
    id: BlockRuleId,
    scenarios: z.array(ScenarioName).min(1).max(TRIGGER_RULE_CAPS.scenarios),
    response: TriggerResponse,
    ban_seconds: z.number().int().min(60).max(BLOCK_RULE_BAN_MAX_SECONDS),
})
    .strict();
/**
 * The triggers CrowdSec should run: enabled, enforcing, by id. Where two name the
 * same scenario the one first by id decides — profiles are tried in order and the
 * first match ends it — which the Rules page says.
 */
export function resolveTriggerRules(rules) {
    const out = [];
    for (const rule of rules) {
        if (rule.kind !== 'trigger' || !rule.enabled || rule.mode !== 'enforce')
            continue;
        out.push({
            id: rule.id,
            scenarios: [...new Set(rule.scenarios)].sort(),
            response: rule.response,
            ban_seconds: rule.ban_seconds,
        });
    }
    return out.sort((a, b) => a.id.localeCompare(b.id));
}
/**
 * `set_crowdsec_triggers` (P11.16): make CrowdSec's profiles hold exactly these
 * triggers, in a marked block at the top of `profiles.yaml`. The whole desired
 * state: none removes the block. Each decision it makes is tagged with its rule,
 * so the blocklist can say which rule banned an address.
 */
export const SetCrowdsecTriggersArgs = z
    .object({
    triggers: z.array(ResolvedTriggerRule).max(BLOCK_RULE_CAPS.rules),
    /** Only say whether anything differs; write nothing (P8.3's alert-only drift). */
    dry_run: z.boolean().optional(),
})
    .strict();
/** The `origin` a trigger's decisions carry in CrowdSec (it prefixes `crowdsec/`). */
export const TRIGGER_ORIGIN_PREFIX = 'bartizo-trigger-';
/** Bartizo's behaviour rules' scenarios, `bartizo/<name>`. */
export const BEHAVIOUR_SCENARIO_PREFIX = 'bartizo/';
/** Bartizo's access logs CrowdSec may read, by the web server that writes them. */
export const BartizoWebLog = z.enum(['nginx', 'apache']);
/**
 * `set_crowdsec_web_rules` (P11.5): make CrowdSec run exactly these behaviour
 * rules — a parser for Bartizo's JSON access log, CrowdSec reading the logs
 * named, a scenario per rule, and the ban each one gives in a marked block at
 * the top of `profiles.yaml`. The whole desired state: no scenarios removes
 * everything Bartizo wrote. Hub scenarios never see this log (its service is
 * `bartizo-http`), so nothing is counted twice.
 */
export const SetCrowdsecWebRulesArgs = z
    .object({
    scenarios: z.array(ResolvedBehaviourRule).max(400),
    logs: z.array(BartizoWebLog).max(2),
    /** Only say whether anything differs; write nothing (P8.3's alert-only drift). */
    dry_run: z.boolean().optional(),
})
    .strict();
/** Whether a path matches a condition, the way the web server and CrowdSec match it. */
export function pathMatches(match, path) {
    const value = match.value.toLowerCase();
    const subject = path.toLowerCase();
    switch (match.kind) {
        case 'exact':
            return subject === value;
        case 'prefix':
            return subject.startsWith(value);
        case 'suffix':
            return subject.endsWith(value);
    }
}
/** Whether a rule's paths catch this path (a rule with none catches every path). */
export const rulePathMatches = (rule, path) => rule.paths.length === 0 || rule.paths.some((match) => pathMatches(match, path));
/** Where a rule in a site's effective list came from. */
export const BlockRuleOrigin = z.object({
    scope: z.enum(['global', 'group', 'server']),
    /** The group's name, for a group layer. */
    name: z.string().max(200).nullable(),
});
/**
 * `GET /v1/block-rules/bans`: how many bans each rule made, over the last `days`
 * days, across the fleet. `rule` is `trigger:<id>` or `behaviour:<id>`; a request
 * rule refuses requests and bans nobody, so it has no row.
 */
export const BlockRuleBans = z.object({
    days: z.number().int().positive(),
    items: z
        .array(z.object({
        rule: z.string().max(200),
        bans: z.number().int().nonnegative(),
        addresses: z.number().int().nonnegative(),
        servers: z.number().int().nonnegative(),
        lastSeen: IsoTime,
    }))
        .max(500),
    /**
     * What CrowdSec's own counters say about each behaviour rule since it last
     * started, summed over the servers whose latest health report is recent: events
     * counted, and times it fired. A rule that counted nothing has no row.
     */
    counting: z
        .array(z.object({
        /** `behaviour:<id>`. */
        rule: z.string().max(200),
        counted: z.number().int().nonnegative(),
        fired: z.number().int().nonnegative(),
        servers: z.number().int().nonnegative(),
    }))
        .max(500)
        .default([]),
    /** Servers with a recent health report; none, and "nothing counted" says nothing. */
    reporting: z.number().int().nonnegative().default(0),
});
/** `GET /v1/block-rules/addresses`: which addresses one rule banned, newest first, in pages. */
export const BlockRuleAddressesQuery = z.object({
    /** `trigger:<id>` or `behaviour:<id>`. */
    rule: z.string().regex(/^(?:trigger|behaviour):[a-z0-9][a-z0-9-]{0,47}$/),
    cursor: z
        .string()
        .regex(/^\d{1,6}$/)
        .optional(),
    limit: z.coerce.number().int().min(1).max(100).default(25),
});
export const BlockRuleAddresses = z.object({
    days: z.number().int().positive(),
    items: z
        .array(z.object({
        ip: z.string().max(64),
        bans: z.number().int().nonnegative(),
        serverCount: z.number().int().nonnegative(),
        servers: z.array(ServerRef).max(20),
        firstSeen: IsoTime,
        lastSeen: IsoTime,
    }))
        .max(100),
    nextCursor: z.string().nullable(),
});
//# sourceMappingURL=block-rules.js.map